import React from "react";
import { useRootStore } from "../../../../store/contexts";
import { ShowReferenceSet } from "../../../common/controls";
import { musicalWorkDisplayId } from "../../../musicalWork/store/MusicalWorkCommonStore";
import { ShowMultiLine } from "../../../common/controls";
import { ShowIntegerChoices } from "../../../common/controls";
import { useSnapshotModalStack } from "../../../common/controls";
import { EditionContractPublisherView as SnapshotEditionContractPublisherView } from "./EditionContractPublisherView";
import { PublisherView as SnapshotPublisherView } from "./PublisherView";
import { PublisherRepresentativeView as SnapshotPublisherRepresentativeView } from "./PublisherRepresentativeView";
import { AuthorComposerView as SnapshotAuthorComposerView } from "./AuthorComposerView";
import { MusicalWorkView as SnapshotMusicalWorkView } from "./MusicalWorkView";
import { TerritoryView as SnapshotTerritoryView } from "./TerritoryView";
import { useFileCellAffordances } from "../../../common/controls";
import {
  useSnapshotExtension,
  useSnapshotExtensionTop,
} from "../../../common/controls";
import { Box, FormControl, Typography } from "@mui/material";

type EditionDocumentationViewProps = {
  snapshotData: any;
  // Propagated from the top-level snapshot view so nested tables can resolve
  // the snapshot-scoped file-download endpoint (/<snapshotEntityName>.data/
  // <snapshotRecordId>/<fileUuid>). Both are optional at the type level but
  // required at runtime for the download / view affordances to work.
  snapshotEntityName?: string;
  snapshotRecordId?: string;
};

export const EditionDocumentationView: React.FC<
  EditionDocumentationViewProps
> = ({ snapshotData, snapshotEntityName, snapshotRecordId }) => {
  const { langStore } = useRootStore();
  const renderExtensionTop = useSnapshotExtensionTop("EditionDocumentation");
  const renderExtension = useSnapshotExtension("EditionDocumentation");

  const { pushModal, modalElement } = useSnapshotModalStack(
    (entityName, data) => {
      switch (entityName) {
        case "EditionContractPublisher":
          return (
            <SnapshotEditionContractPublisherView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "Publisher":
          return (
            <SnapshotPublisherView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "PublisherRepresentative":
          return (
            <SnapshotPublisherRepresentativeView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "AuthorComposer":
          return (
            <SnapshotAuthorComposerView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "MusicalWork":
          return (
            <SnapshotMusicalWorkView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "Territory":
          return (
            <SnapshotTerritoryView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        default:
          return null;
      }
    }
  );
  const { renderFileCell, excelViewerElement } = useFileCellAffordances({
    snapshotEntityName,
    snapshotRecordId,
    enableDownload: true,
  });
  return (
    <>
      {renderExtensionTop ? renderExtensionTop(snapshotData) : null}
      <FormControl fullWidth>
        <ShowReferenceSet
          label={langStore.EditionDocumentationWorksLabel}
          selectedValues={(snapshotData?.works ?? []).map(
            (__x: any, __i: number) => ({
              id: String(__i),
              displayId: musicalWorkDisplayId(__x, langStore),
            })
          )}
          onView={(id) => {
            const idx = parseInt(id, 10);
            pushModal("MusicalWork", (snapshotData?.works ?? [])[idx]);
          }}
        />
        <ShowMultiLine
          label={langStore.EditionDocumentationAuthorsDisplayLabel}
          value={String(snapshotData?.authorsDisplay ?? "")}
        />
        <ShowMultiLine
          label={
            langStore.EditionDocumentationPublisherConfigurationDisplayLabel
          }
          value={String(snapshotData?.publisherConfigurationDisplay ?? "")}
        />
        <ShowMultiLine
          label={langStore.EditionDocumentationTerritoriesDisplayLabel}
          value={String(snapshotData?.territoriesDisplay ?? "")}
        />
        <ShowIntegerChoices
          label={langStore.EditionDocumentationContractDurationLabel}
          value={snapshotData?.contractDuration ?? null}
          unitSingular={"año"}
          unitPlural={"años"}
          nullText={langStore.EditionDocumentationContractDurationNullText}
        />
        <ShowIntegerChoices
          label={langStore.EditionDocumentationContractRenewalLabel}
          value={snapshotData?.contractRenewal ?? null}
          unitSingular={"año"}
          unitPlural={"años"}
          nullText={langStore.EditionDocumentationContractRenewalNullText}
        />
        <ShowMultiLine
          label={langStore.EditionDocumentationContractNumberLabel}
          value={String(snapshotData?.contractNumber ?? "")}
        />
        <Box sx={{ my: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            {langStore.EditionDocumentationFinalDocumentPdfLabel}
          </Typography>
          {snapshotData?.finalDocumentPdf?.filename ? (
            <Box sx={{ display: "flex" }}>
              {renderFileCell(
                {
                  finalDocumentPdf: snapshotData?.finalDocumentPdf.filename,
                  finalDocumentPdf_data: snapshotData?.finalDocumentPdf,
                },
                "finalDocumentPdf",
                snapshotData?.finalDocumentPdf?.mimetype ?? "",
                snapshotData?.finalDocumentPdf.filename
              )}
            </Box>
          ) : null}
        </Box>
      </FormControl>
      {renderExtension ? renderExtension(snapshotData) : null}

      {modalElement}
      {excelViewerElement}
    </>
  );
};
